/* eslint-disable react-refresh/only-export-components */
import React from 'react';

import { gql, useMutation, useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Add, Close, EditCalendar, Notes, Person, Remove, Schedule, Star } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link as LinkMui,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { MobileTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {
  clamp,
  compact,
  concat,
  every,
  filter,
  find,
  get,
  includes,
  isArray,
  isEmpty,
  isNil,
  join,
  map,
  max,
  min,
  pick,
  range,
  reduce,
  size,
  snakeCase,
  some,
  sortBy,
  toString,
  uniq,
} from 'lodash';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import DatePicker, { DateObject, getAllDatesInRange } from 'react-multi-date-picker';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import {
  BottomSafeArea,
  ErrorMessageBox,
  LoadingButton,
  LoadingSpinner,
  NumberTextField,
  RadioButtonGroup,
  RadioCard,
  UniformCard,
} from '../../../../components';
import { CreditNotEnoughPopup } from '../../../../components/credit_not_enough_popup';
import { RestaurantSelector } from '../../../../components/nav_bar/restaurant_selector';
import Protector from '../../../../components/protector';
import { AppContext } from '../../../../contexts/app_context';
import { createScopedI18n, i18n } from '../../../../i18n/i18n';
import { errorsPairI18n, formatDate, hookFormNestedFieldErrorMessage, labelFieldEnum } from '../../../../utils/libs';
import Snackbar, { INSUFFICIENT_BALANCE } from '../../../../utils/snackbar';
import Stash, { CREATE_JOBS } from '../../../../utils/stash';
import { buildIdempotencyKey } from '../../../../utils/idempotency';
import JobCardListDrawer from './job_card_list_drawer';

const REQUIRED_DAY_COUNT_RANGE = { min: 1, max: 3 };
export const MIN_SCHEDULE_DAYS = 3;
export const MAX_SCHEDULE_DAYS = 12;
export const MAX_SCHEDULE_DATE_LENGTH = 7;
const DEFAULT_MAX_NUMBER_OF_POSITION = 20;

const jobCreatePageI18n = createScopedI18n('pages.jobs_new');
const periodJobCreatePageI18n = createScopedI18n('pages.period_jobs_new');
const jobFieldI18n = createScopedI18n('graphql.fields.job');
const jobErrorI18n = createScopedI18n('graphql.errors.types.job.fields', { joinOutput: true });

const jobCreatesSchema = Yup.object({
  isPeriod: Yup.boolean().default(true),
  needReplacement: Yup.boolean().default(false),
  requiredDayCount: Yup.number()
    .when('isPeriod', { is: true, then: (schema) => schema.required(jobErrorI18n('required_day_count.blank')) })
    .min(
      REQUIRED_DAY_COUNT_RANGE.min,
      jobErrorI18n('required_day_count.too_short', { count: REQUIRED_DAY_COUNT_RANGE.min }),
    )
    .max(
      REQUIRED_DAY_COUNT_RANGE.max,
      jobErrorI18n('required_day_count.too_long', { count: REQUIRED_DAY_COUNT_RANGE.max }),
    ),

  bidPrices: Yup.array()
    .of(
      Yup.object({
        scheduleDate: Yup.string().required(),
        isBidPrice: Yup.boolean().default(false),
        restaurantBidPricePerHour: Yup.number()
          .nullable()
          .when('isBidPrice', {
            is: true,
            then: (schema) => schema.required(jobErrorI18n('restaurant_bid_price_per_hour.blank')),
          }),
      }),
    )
    .default([]),

  // JobInput
  scheduleDates: Yup.array()
    .of(
      Yup.lazy(() =>
        Yup.date()
          .required(jobErrorI18n('schedule_date.blank'))
          .min(
            dayjs().startOf('day').add(MIN_SCHEDULE_DAYS, 'days'),
            jobErrorI18n('schedule_date.too_short', { count: MIN_SCHEDULE_DAYS }),
          )
          .max(
            dayjs().endOf('day').add(MAX_SCHEDULE_DAYS, 'days'),
            jobErrorI18n('schedule_date.too_long', { count: MAX_SCHEDULE_DAYS }),
          ),
      ),
    )
    .required(jobErrorI18n('schedule_dates.blank'))
    .when('requiredDayCount', ([requiredDayCount], schema) =>
      schema.min(
        requiredDayCount ?? REQUIRED_DAY_COUNT_RANGE.min,
        jobErrorI18n('schedule_dates.too_short', { count: requiredDayCount ?? REQUIRED_DAY_COUNT_RANGE.min }),
      ),
    )
    .max(MAX_SCHEDULE_DATE_LENGTH, jobErrorI18n('schedule_dates.too_long', { count: MAX_SCHEDULE_DATE_LENGTH }))
    .transform((value) => (isEmpty(value) ? value : sortBy(value)))
    .test('is_continuous', jobErrorI18n('schedule_dates.not_continuous'), (values) => {
      if (!isArray(values) || size(values) < 2) return true;

      return every(values, (value, index) =>
        index === 0 ? true : dayjs(value).diff(dayjs(values[index - 1]), 'day') === 1,
      );
    }),
  startTime: Yup.date()
    .typeError(jobErrorI18n('job_start_at.invalid'))
    .required(jobErrorI18n('job_start_at.blank'))
    .test({ message: jobErrorI18n('job_start_at.invalid'), test: (startTime) => startTime.getMinutes() % 30 === 0 }),
  endTime: Yup.date()
    .typeError(jobErrorI18n('job_end_at.invalid'))
    .required(jobErrorI18n('job_end_at.blank'))
    .test({ message: jobErrorI18n('job_end_at.invalid'), test: (endTime) => endTime.getMinutes() % 30 === 0 }),
  breakDurationMinute: Yup.number()
    .required(jobErrorI18n('break_duration.blank'))
    .min(0, jobErrorI18n('break_duration.too_short', { count: 0 })),
  gender: Yup.string()
    .required(jobErrorI18n('gender.blank'))
    .oneOf(['male', 'female', 'SET_NULL_ON_SUBMIT'], jobErrorI18n('gender.invalid')),
  jobType: Yup.string().required(jobErrorI18n('job_type.blank')),
  duties: Yup.array()
    .of(Yup.string().required(jobErrorI18n('duty.blank')))
    .when('jobType', ([jobType], schema) =>
      isNil(jobType) || jobType === 'other'
        ? schema
        : schema.required(jobErrorI18n('duty.blank')).min(1, jobErrorI18n('duty.blank')),
    ),
  remark: Yup.string()
    .nullable()
    .when('jobType', ([jobType], schema) =>
      isNil(jobType) || jobType === 'other' ? schema.required(jobErrorI18n('remark.blank')) : schema,
    ),
  detail: Yup.string().nullable(),
  numberOfPosition: Yup.number()
    .required(jobErrorI18n('number_of_position.blank'))
    .min(1, jobErrorI18n('number_of_position.too_short', { count: 1 })),
  uniformId: Yup.string().required(jobErrorI18n('uniform.blank')),
  uniform: Yup.string()
    .nullable()
    .when('uniformId', ([uniformId], schema) =>
      uniformId === '_OTHER_UNIFORM' ? schema.required(jobErrorI18n('uniform.blank')) : schema,
    ),
  benefit: Yup.string().required(jobErrorI18n('benefit.blank')),
  skillIds: Yup.array().of(Yup.string().required()).default([]),
});

export type JobPricingRequirement = {
  jobStartAt: dayjs.Dayjs;
  jobEndAt: dayjs.Dayjs;
  breakDurationMinute?: number;
  bidPricePerHour?: number;
  skillsId?: string[];
};

export type JobPricing = {
  date: string;
  minPricePerHour: number;
  maxPricePerHour: number;
  minNormalPricePerHour: number;
  maxNormalPricePerHour: number;
  minOtPricePerHour: number;
  maxOtPricePerHour: number;
  minTotalPrice: number;
  maxTotalPrice: number;
  operationMinute: number;
  otMinute: number;
  normalMinute: number;
  minBidAllowed: number;
};

export type JobCreatesSchemaType = Yup.InferType<typeof jobCreatesSchema> & {
  scheduleDateTimes?: {
    scheduleDate: string;
    jobStartAt: dayjs.Dayjs;
    jobEndAt: dayjs.Dayjs;
  }[];
  jobPrices?: JobPricing[];
  jobPricesNormal?: JobPricing[];
};

const mapValueKeyToI18nField = (valueKey: string) => {
  const valueKeyI18nFieldMap: { [valueKey: string]: string } = {
    scheduleDates: 'schedule_dates',
    startTime: 'start_time',
    endTime: 'end_time',
    breakDurationMinute: 'break_duration_minute',
    gender: 'gender',
    jobType: 'job_type',
    duties: 'duty',
    remark: 'remark',
    detail: 'detail',
    numberOfPosition: 'number_of_position',
    uniform: 'uniform',
    benefit: 'benefit',
    restaurantBidPricePerHour: 'restaurantBidPricePerHour',
    skills: 'skills',
  };

  return valueKeyI18nFieldMap[valueKey] ?? snakeCase(valueKey);
};

const jobCreatingOptionGql = gql(`
  query jobCreatingOption($businessType: BUSINESS_TYPE_OPTIONS!) {
    jobCreatingOption(businessType: $businessType) {
      maxNumberOfPosition
      otHour
      pricing {
        id
        price
        priceType
      }
      cancelFee {
        id
        price
        priceType
      }
      enumJobTypesAndDuties {
        id
        duties
        defaultDuties
      }
      pricingRange {
        id
        min
        max
        priceType
      }
      skills {
        id
        name
        description
        additionalWagePerHour
      }
      uniforms {
        id
        title
        jobType
        photo
      }
    }
  }
`);

const jobPricingGql = gql(`
  query jobPricing($requirements: [JobPricingInput!]!) {
    jobPricing(requirements: $requirements) {
      date
      minPricePerHour
      maxPricePerHour
      minNormalPricePerHour
      maxNormalPricePerHour
      minOtPricePerHour
      maxOtPricePerHour
      minTotalPrice
      maxTotalPrice
      operationMinute
      otMinute
      normalMinute
      minBidAllowed
    }
  }
`);

const createJobsGql = gql(`
  mutation createJobs($isPeriod: Boolean, $requiredDayCount: Int, $needReplacement: Boolean, $jobs: [JobInput!]!) {
    createJobs(
      isPeriod: $isPeriod
      requiredDayCount: $requiredDayCount
      needReplacement: $needReplacement
      jobs: $jobs
    ) {
      success
      errors
    }
  }
`);

const JobCreatePeriodPage = () => {
  const { contextCurrentRestaurant } = React.useContext(AppContext);

  const navigate = useNavigate();

  const [showDialog, setShowDialog] = React.useState(false);
  const openDialogHandler = () => setShowDialog(true);
  const closeDialogHandler = () => setShowDialog(false);

  const createJobForm = useForm<JobCreatesSchemaType>({
    defaultValues: {
      isPeriod: true,

      needReplacement: true,
      scheduleDates: [],
      breakDurationMinute: 60,
      gender: 'SET_NULL_ON_SUBMIT',
      jobType: 'other',
      duties: [],
      numberOfPosition: 1,
      bidPrices: [],
      skillIds: [],
    },
    resolver: yupResolver(jobCreatesSchema) as never,
  });
  const {
    control,
    getValues,
    setValue,
    watch,
    reset,
    handleSubmit,
    getFieldState,
    formState: { isSubmitting, errors: validateErrors },
  } = createJobForm;

  const jobType = watch('jobType');
  const skillIds = watch('skillIds');
  const bidPrices = watch('bidPrices');

  const scheduleDates = watch('scheduleDates');
  const scheduleDateTimes = watch('scheduleDateTimes');
  const startTime = watch('startTime');
  const endTime = watch('endTime');
  const breakDurationMinute = watch('breakDurationMinute');

  const [isNotEnoughCredit, setIsNotEnoughCredit] = React.useState(false);

  // Sync data between fields
  React.useEffect(() => {
    const nextBidPrices = map(scheduleDates, (scheduleDate) => {
      const dateString = dayjs(scheduleDate).format('YYYY-MM-DD');
      return (
        find(getValues('bidPrices'), (bidPriceField) => bidPriceField.scheduleDate === dateString) ?? {
          scheduleDate: dateString,
          isBidPrice: false,
        }
      );
    });

    setValue('bidPrices', nextBidPrices);
  }, [scheduleDates]);

  React.useEffect(() => {
    if (isEmpty(scheduleDates) || isNil(startTime) || isNil(endTime)) {
      setValue('scheduleDateTimes', []);
      return;
    }

    const nextScheduleDateTimes = map(scheduleDates, (scheduleDate) => {
      const jobStartAt = dayjs(scheduleDate)
        .hour(dayjs(startTime).hour())
        .minute(dayjs(startTime).minute())
        .second(0)
        .millisecond(0);

      let jobEndAt = dayjs(scheduleDate)
        .hour(dayjs(endTime).hour())
        .minute(dayjs(endTime).minute())
        .second(0)
        .millisecond(0);

      if (jobEndAt.isBefore(jobStartAt)) {
        jobEndAt = jobEndAt.add(1, 'day');
      }

      return { scheduleDate: dayjs(scheduleDate).format('YYYY-MM-DD'), jobStartAt, jobEndAt };
    });

    setValue('scheduleDateTimes', nextScheduleDateTimes);
  }, [scheduleDates, startTime, endTime]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const datePickerRef = React.useRef<any>(null);
  const [showJobCardsDrawer, setShowJobCardsDrawer] = React.useState(false);
  const [bidPriceRange, setBidPriceRange] = React.useState<{ min: number | null; max: number | null }>({
    min: null,
    max: null,
  });
  const bidPriceRangeString = React.useMemo(() => {
    if (isNil(bidPriceRange.min) || isNil(bidPriceRange.max)) {
      return jobCreatePageI18n('dynamic_price');
    }
    if (bidPriceRange.min === bidPriceRange.max) {
      return bidPriceRange.max;
    }
    return `${bidPriceRange.min} - ${bidPriceRange.max}`;
  }, [bidPriceRange.min, bidPriceRange.max]);

  // JobCreatingOption
  const { data: jobCreatingOptionData, loading: jobCreatingOptionLoading } = useQuery(jobCreatingOptionGql, {
    variables: { businessType: contextCurrentRestaurant?.businessType },
    skip: isNil(contextCurrentRestaurant?.businessType),
  });

  const maxNumberOfPosition: number =
    jobCreatingOptionData?.jobCreatingOption?.maxNumberOfPosition ?? DEFAULT_MAX_NUMBER_OF_POSITION;
  const otHour = jobCreatingOptionData?.jobCreatingOption?.otHour ?? 0;
  const cancelFees = reduce(
    jobCreatingOptionData?.jobCreatingOption?.cancelFee,
    (prev, { priceType, price }) => ({ ...prev, [priceType]: price }),
    {},
  );

  const jobTypeOptionWithOther = React.useMemo(
    () =>
      some(jobCreatingOptionData?.jobCreatingOption?.enumJobTypesAndDuties, ({ id }) => id === 'other')
        ? jobCreatingOptionData?.jobCreatingOption?.enumJobTypesAndDuties
        : concat(jobCreatingOptionData?.jobCreatingOption?.enumJobTypesAndDuties ?? [], {
            id: 'other',
            duties: [],
            defaultDuties: [],
          }),
    [jobCreatingOptionData?.jobCreatingOption?.enumJobTypesAndDuties],
  );

  const jobDutyOptions = React.useMemo(
    () => find(jobTypeOptionWithOther, (jobAndDuty) => jobAndDuty.id === jobType),
    [jobType, jobTypeOptionWithOther],
  );

  const findSkill = React.useCallback(
    (skillId: string) => find(jobCreatingOptionData?.jobCreatingOption?.skills, (skill) => skill.id === skillId),
    [jobCreatingOptionData?.jobCreatingOption?.skills],
  );

  const uniformId = watch('uniformId');
  const jobTypedUniforms = React.useMemo(
    () => filter(jobCreatingOptionData?.jobCreatingOption?.uniforms, (uniform) => uniform.jobType === jobType),
    [jobCreatingOptionData?.jobCreatingOption?.uniforms, jobType],
  );
  const selectedUniform = React.useMemo(
    () => find(jobTypedUniforms, (uniform) => uniform.id === uniformId),
    [uniformId, jobTypedUniforms],
  );

  React.useEffect(() => {
    if (contextCurrentRestaurant === null) return;

    const stash = Stash(CREATE_JOBS);
    const key = contextCurrentRestaurant.id.toString();
    const values = stash.load(key);

    // monkey patch
    if (values.startTime) values.startTime = dayjs(values.startTime);
    if (values.endTime) values.endTime = dayjs(values.endTime);

    if (values.jobType && !find(jobTypeOptionWithOther, ({ id }) => id === values.jobType)) {
      values.jobType = 'other';
    }

    reset(values, { keepDefaultValues: true });
  }, [reset, contextCurrentRestaurant, jobTypeOptionWithOther]);

  // Handle all set/clear uniformId base on available uniforms
  // * Show normal uniform text input if no available jobTyped uniforms
  // * Set saved uniformId if user not touch it yet and saved uniformId in available jobTyped uniforms
  // * Clear uniformId if currentUniformId not in jobTyped uniforms (but uniforms not empty)
  React.useEffect(() => {
    if (jobCreatingOptionLoading) return;

    const currentUniformId = getValues('uniformId');
    const currentUniformIdStatus = getFieldState('uniformId');
    const jobTypedUniformIds = map(jobTypedUniforms, 'id');

    const stashValues = contextCurrentRestaurant
      ? Stash(CREATE_JOBS).load(toString(contextCurrentRestaurant.id))
      : null;
    const savedUniformId: string | undefined = get(stashValues, 'uniformId');

    if (isEmpty(jobTypedUniforms)) {
      setValue('uniformId', '_OTHER_UNIFORM', { shouldValidate: true });
    } else if (
      !currentUniformIdStatus.isTouched &&
      !isNil(savedUniformId) &&
      includes(jobTypedUniformIds, savedUniformId)
    ) {
      setValue('uniformId', savedUniformId);
    } else if (!includes(jobTypedUniformIds, currentUniformId)) {
      // @ts-expect-error Reset uniformId to null. Make user select again.
      setValue('uniformId', null);
    }
  }, [jobCreatingOptionLoading, jobTypedUniforms, contextCurrentRestaurant]);

  const uniformErrorMessage = React.useMemo(
    () => (uniformId === '_OTHER_UNIFORM' ? validateErrors.uniform?.message : validateErrors.uniformId?.message),
    [uniformId, validateErrors.uniform, validateErrors.uniformId],
  );

  // Fetch jobPricing from server
  const [jobPricingRequirements, jobPricingNormalRequirements] = React.useMemo<
    [JobPricingRequirement[], JobPricingRequirement[]] | [null, null]
  >(() => {
    if (isEmpty(scheduleDateTimes) || isNil(breakDurationMinute)) {
      return [null, null];
    }

    const pricingRequirements = map(scheduleDateTimes, ({ scheduleDate, jobStartAt, jobEndAt }) => {
      const bidPrice = find(bidPrices, ({ scheduleDate: bidScheduleDate }) => bidScheduleDate === scheduleDate);
      const bidPricePerHour = bidPrice?.isBidPrice ? bidPrice?.restaurantBidPricePerHour ?? undefined : undefined;
      return { jobStartAt, jobEndAt, breakDurationMinute, bidPricePerHour, skillIds };
    });

    const pricingNormalRequirements = map(pricingRequirements, (pricing) => ({
      ...pricing,
      bidPricePerHour: undefined,
    }));

    return [pricingRequirements, pricingNormalRequirements];
  }, [scheduleDateTimes, breakDurationMinute, bidPrices, skillIds]);
  const deferredPricingRequirements = React.useDeferredValue(jobPricingRequirements);
  const deferredPricingNormalRequirements = React.useDeferredValue(jobPricingNormalRequirements);

  const { data: jobPricingData, loading: jobPricingLoading } = useQuery(jobPricingGql, {
    fetchPolicy: 'cache-first',
    variables: { requirements: deferredPricingRequirements },
    skip: isEmpty(deferredPricingRequirements),
  });
  React.useEffect(() => {
    if (jobPricingLoading || isEmpty(jobPricingData?.jobPricing)) return;

    setValue('jobPrices', [...(jobPricingData?.jobPricing ?? [])]);
  }, [jobPricingLoading, jobPricingData]);

  const { data: jobPricingNormalData, loading: jobPricingNormalLoading } = useQuery(jobPricingGql, {
    fetchPolicy: 'cache-first',
    variables: { requirements: deferredPricingNormalRequirements },
    skip: isEmpty(deferredPricingNormalRequirements),
  });
  React.useEffect(() => {
    if (jobPricingNormalLoading || isEmpty(jobPricingNormalData?.jobPricing)) return;

    const nextPriceRange = {
      min: max(map(jobPricingNormalData?.jobPricing, (p) => p.minTotalPrice as number)) ?? null,
      max: max(map(jobPricingNormalData?.jobPricing, (p) => p.maxTotalPrice as number)) ?? null,
    };

    setValue('jobPricesNormal', [...(jobPricingNormalData?.jobPricing ?? [])]);
    setBidPriceRange(nextPriceRange);
  }, [jobPricingNormalLoading, jobPricingNormalData]);

  // Field handlers
  const numberOfPositionHandler = (positionNumber: number | null) => {
    if (isNil(positionNumber)) {
      // @ts-expect-error Let user fix this by enter something to form.
      setValue('numberOfPosition', null);
      return;
    }

    let nextValue = positionNumber;
    if (!Number.isFinite(nextValue)) {
      return;
    }

    if (maxNumberOfPosition !== -1) {
      nextValue = clamp(nextValue, 0, maxNumberOfPosition);
    } else {
      nextValue = nextValue < 0 ? 0 : nextValue;
    }
    setValue('numberOfPosition', nextValue);
  };

  const jobDutiesHandler = (checked: boolean, value: string) => {
    let newDuties = getValues('duties') ?? [];
    if (checked) {
      newDuties = uniq([value, ...newDuties]);
    } else {
      newDuties = filter(newDuties, (duty) => duty !== value);
    }
    setValue('duties', newDuties, { shouldTouch: true });
  };

  const [createJobsMutation] = useMutation(createJobsGql);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [hasCreateFailure, setHasCreateFailure] = React.useState(false);

  React.useEffect(() => {
    const subscription = watch(() => {
      if (!hasCreateFailure) return;
      setHasCreateFailure(false);
      setErrorMessage(null);
    });
    return () => subscription.unsubscribe();
  }, [watch, hasCreateFailure]);

  const prefillFormData = (prefillData: JobCreatesSchemaType) => {
    if (contextCurrentRestaurant === null) return;

    const stash = Stash(CREATE_JOBS);
    const key = contextCurrentRestaurant.id.toString();
    const values = pick(prefillData, [
      'needReplacement',
      'startTime',
      'endTime',
      'breakDurationMinute',
      'gender',
      'jobType',
      'duties',
      'remark',
      'detail',
      'numberOfPosition',
      'uniform',
      'benefit',
      'skillIds',
      'uniformId',
    ]);
    stash.save(key, values);
  };

  const submitHandler = async (values: JobCreatesSchemaType) => {
    const restaurantId = contextCurrentRestaurant?.id;
    if (restaurantId == null) return;
    if (hasCreateFailure) return;

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const uniformId = values.uniformId === '_OTHER_UNIFORM' ? null : values.uniformId;
    const uniformText = values.uniformId === '_OTHER_UNIFORM' ? values.uniform : null;
    const remarkText = join(
      compact(values.jobType === 'other' ? [values.detail, uniformText, values.remark] : [uniformText, values.detail]),
      '\n\n',
    );

    const jobInput = {
      restaurantId,

      benefit: values.benefit,
      breakDurationMinute: values.breakDurationMinute,
      duties: values.jobType === 'other' ? [] : values.duties,
      gender: values.gender === 'SET_NULL_ON_SUBMIT' ? null : values.gender,
      jobType: values.jobType,
      numberOfPosition: values.numberOfPosition,
      remark: remarkText,
      uniformId,
      skillIds: values.skillIds,
    };

    const jobInputs = map(values.scheduleDates, (scheduleDate) => {
      const jobStartAt = dayjs(scheduleDate)
        .hour(dayjs(values.startTime).hour())
        .minute(dayjs(values.startTime).minute())
        .second(0)
        .millisecond(0);

      let jobEndAt = dayjs(scheduleDate)
        .hour(dayjs(values.endTime).hour())
        .minute(dayjs(values.endTime).minute())
        .second(0)
        .millisecond(0);

      if (jobEndAt.isBefore(jobStartAt)) {
        jobEndAt = jobEndAt.add(1, 'day');
      }

      const restaurantBidPricePerHour =
        find(
          values.bidPrices,
          ({ scheduleDate: bidScheduleDate, isBidPrice }) =>
            isBidPrice && bidScheduleDate === dayjs(scheduleDate).format('YYYY-MM-DD'),
        )?.restaurantBidPricePerHour ?? null;

      return {
        ...jobInput,
        jobStartAt: jobStartAt.toISOString(),
        jobEndAt: jobEndAt.toISOString(),
        restaurantBidPricePerHour,
      };
    });

    const variable = {
      isPeriod: values.isPeriod,
      needReplacement: values.needReplacement,
      requiredDayCount: values.requiredDayCount,
      jobs: jobInputs,
    };

    try {
      const idempotencyKey = buildIdempotencyKey(`create-jobs-${restaurantId}`);
      const { data } = await createJobsMutation({
        variables: variable,
        context: {
          headers: {
            'x-idempotency-key': idempotencyKey,
          },
        },
      });

      if (data?.createJobs?.success) {
        prefillFormData(values);
        navigate('/jobs');
      } else {
        setHasCreateFailure(true);
        const errorsPair = data?.createJobs?.errors ?? [];

        if (
          includes(
            map(errorsPair, (e) => join(e)),
            join(['credit', 'not_enough']),
          )
        ) {
          setIsNotEnoughCredit(true);
          Snackbar.enqueueError(INSUFFICIENT_BALANCE);
        }

        const nextErrorMessage = errorsPairI18n(jobErrorI18n, errorsPair);
        setErrorMessage(nextErrorMessage);
      }
    } catch (_error) {
      setHasCreateFailure(true);
      setErrorMessage(periodJobCreatePageI18n('create_request_failed_message'));
    }
  };

  if (isNil(contextCurrentRestaurant)) {
    return (
      <Protector permissions={['periodJobCreate']}>
        <Protector.Rejected>
          <Container>
            <ErrorMessageBox
              hideRefreshButton
              errorMessage={periodJobCreatePageI18n('no_permission')}
              goToButton={
                <Button component={Link} to="/" variant="outlined" fullWidth>
                  {periodJobCreatePageI18n('no_permission_button')}
                </Button>
              }
              BoxProps={{ style: { height: '100%' } }}
            />
          </Container>
        </Protector.Rejected>
        <Protector.Allowed>
          <Container
            maxWidth="sm"
            sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <Stack gap={1}>
              <Alert severity="warning">
                <AlertTitle>{i18n.t('errors.messages.restaurant_branch.blank')}</AlertTitle>
                {i18n.t('errors.messages.restaurant_branch.blank_detail')}
              </Alert>
              <RestaurantSelector
                variant="outlined"
                fullWidth
                sx={{ padding: 1 }}
                data-testid="restaurant-selector-warning"
              />
            </Stack>
          </Container>
        </Protector.Allowed>
      </Protector>
    );
  }

  return (
    <Protector permissions={['periodJobCreate']}>
      <Protector.Rejected>
        <Container>
          <ErrorMessageBox
            hideRefreshButton
            errorMessage={periodJobCreatePageI18n('no_permission')}
            goToButton={
              <Button component={Link} to="/" variant="outlined" fullWidth>
                {periodJobCreatePageI18n('no_permission_button')}
              </Button>
            }
            BoxProps={{ style: { height: '100%' } }}
          />
        </Container>
      </Protector.Rejected>
      <Protector.Allowed>
        <FormProvider {...createJobForm}>
          <Container maxWidth="sm" sx={{ paddingY: 2 }}>
            <form onSubmit={handleSubmit(submitHandler)}>
              <Grid container gap={2}>
                <Grid container item gap={1}>
                  <Grid item xs={3}>
                    <Stack minHeight={56} flexDirection={'row'} alignItems={'center'}>
                      <FormLabel error={isNil(contextCurrentRestaurant)}>{jobCreatePageI18n('restaurant')} *</FormLabel>
                    </Stack>
                  </Grid>
                  <Grid container item xs={9} sx={{ marginRight: -1 }}>
                    <RestaurantSelector fullWidth />
                  </Grid>
                </Grid>

                <Controller
                  key="scheduleDates"
                  name="scheduleDates"
                  control={control}
                  render={({ field: { name, value, onBlur, onChange }, fieldState: { invalid, error } }) => (
                    <FormControl id={name} fullWidth error={invalid}>
                      <Grid container item alignItems={'center'} gap={1}>
                        <Grid item xs={3}>
                          <FormLabel>{jobFieldI18n('schedule_dates')} *</FormLabel>
                        </Grid>
                        <Grid item xs="auto">
                          <DatePicker
                            ref={datePickerRef}
                            range
                            minDate={dayjs().startOf('day').add(MIN_SCHEDULE_DAYS, 'days').toDate()}
                            maxDate={dayjs().endOf('day').add(MAX_SCHEDULE_DAYS, 'days').toDate()}
                            value={value}
                            onChange={(dates: DateObject[] | null) => {
                              onChange(dates ? getAllDatesInRange(dates, true) : []);
                              onBlur();
                            }}
                            render={
                              <IconButton>
                                <EditCalendar />
                              </IconButton>
                            }
                          />
                        </Grid>

                        <Grid item xs overflow="auto">
                          <Button
                            color="inherit"
                            fullWidth
                            size="small"
                            onClick={() => datePickerRef.current?.openCalendar?.()}
                            sx={{ justifyContent: 'flex-start', minHeight: 48 }}
                          >
                            {isEmpty(value) ? (
                              <Typography variant="subtitle2" textAlign={'start'} sx={{ opacity: 0.6 }}>
                                {jobCreatePageI18n('schedule_date_range_hint')}
                              </Typography>
                            ) : (
                              <Chip
                                color="primary"
                                label={
                                  size(value) === 1
                                    ? formatDate(min(value))
                                    : `${formatDate(min(value))} - ${formatDate(max(value))}`
                                }
                              />
                            )}
                          </Button>
                        </Grid>
                      </Grid>

                      <Grid container item gap={1} justifyContent={'flex-end'}>
                        <Grid item xs={9}>
                          <FormHelperText>
                            {hookFormNestedFieldErrorMessage(error, { joinErrorMessages: false })}
                          </FormHelperText>
                        </Grid>
                      </Grid>
                    </FormControl>
                  )}
                />

                {/** startTime, endTime */}
                <Grid container item gap={1}>
                  <Grid item xs={3}>
                    <Stack minHeight={56} flexDirection={'row'} alignItems={'center'}>
                      <FormLabel error={!isEmpty(validateErrors.startTime) || !isEmpty(validateErrors.endTime)}>
                        {jobCreatePageI18n('job_time')} *
                      </FormLabel>
                    </Stack>
                  </Grid>

                  <Grid item container xs>
                    <Controller
                      key="startTime"
                      name="startTime"
                      control={control}
                      render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                        <FormControl id={field.name} fullWidth error={invalid}>
                          <Grid container item alignItems={'center'} gap={1}>
                            <Grid item xs="auto">
                              <FormLabel>{jobCreatePageI18n('job_time_from')}</FormLabel>
                            </Grid>
                            <Grid item xs>
                              <MobileTimePicker
                                ampm={false}
                                closeOnSelect
                                minutesStep={30}
                                value={value ?? null}
                                slotProps={{ textField: { InputProps: { endAdornment: <Schedule /> } } }}
                                {...field}
                              />
                            </Grid>
                          </Grid>
                          <Grid container item gap={1} justifyContent={'flex-end'}>
                            <FormHelperText>{error?.message}</FormHelperText>
                          </Grid>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item container xs>
                    <Controller
                      key="endTime"
                      name="endTime"
                      control={control}
                      render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                        <FormControl id={field.name} fullWidth error={invalid}>
                          <Grid container item alignItems={'center'} gap={1}>
                            <Grid item xs="auto">
                              <FormLabel>{jobCreatePageI18n('job_time_to')}</FormLabel>
                            </Grid>
                            <Grid item xs>
                              <MobileTimePicker
                                ampm={false}
                                closeOnSelect
                                minutesStep={30}
                                value={value ?? null}
                                slotProps={{ textField: { InputProps: { endAdornment: <Schedule /> } } }}
                                {...field}
                              />
                            </Grid>
                          </Grid>
                          <Grid container item gap={1} justifyContent={'flex-end'}>
                            <FormHelperText>{error?.message}</FormHelperText>
                          </Grid>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>

                <Controller
                  key="requiredDayCount"
                  name="requiredDayCount"
                  control={control}
                  render={({ field: { name, onBlur, onChange, value }, fieldState: { invalid, error } }) => (
                    <FormControl id={name} fullWidth error={invalid}>
                      <Grid container item alignItems={'center'} gap={1}>
                        <Grid item xs={3}>
                          <FormLabel>{jobFieldI18n('required_day_count')} *</FormLabel>
                        </Grid>
                        <Grid item xs overflow={'auto'} whiteSpace={'nowrap'}>
                          <RadioButtonGroup
                            id={name}
                            color="primary"
                            value={value}
                            onChangeValue={onChange}
                            onBlur={onBlur}
                            items={map(
                              range(REQUIRED_DAY_COUNT_RANGE.min, REQUIRED_DAY_COUNT_RANGE.max + 1),
                              (dayCount) => ({
                                label: jobFieldI18n('required_day', { count: dayCount }),
                                value: dayCount,
                              }),
                            )}
                          />
                        </Grid>
                      </Grid>
                      <Grid container item gap={1} justifyContent={'flex-end'}>
                        <Grid item xs={9}>
                          <FormHelperText>{error?.message}</FormHelperText>
                        </Grid>
                      </Grid>
                    </FormControl>
                  )}
                />

                <Controller
                  key="breakDurationMinute"
                  name="breakDurationMinute"
                  control={control}
                  render={({ field: { name, onBlur, onChange, value }, fieldState: { invalid, error } }) => (
                    <FormControl id={name} fullWidth error={invalid}>
                      <Grid container item alignItems={'center'} gap={1}>
                        <Grid item xs={3}>
                          <FormLabel>{jobFieldI18n('break_duration')} *</FormLabel>
                        </Grid>
                        <Grid item xs overflow={'auto'} whiteSpace={'nowrap'}>
                          <RadioButtonGroup
                            id={name}
                            color="primary"
                            value={value}
                            onChangeValue={onChange}
                            onBlur={onBlur}
                            items={[
                              { label: jobFieldI18n('break_duration_hour.zero'), value: 0 },
                              { label: jobFieldI18n('break_duration_hour.one'), value: 60 },
                              { label: jobFieldI18n('break_duration_hour.one_and_half'), value: 90 },
                              { label: jobFieldI18n('break_duration_hour.two'), value: 120 },
                            ]}
                          />
                        </Grid>
                      </Grid>
                      <Grid container item gap={1} justifyContent={'flex-end'}>
                        <Grid item xs={9}>
                          <FormHelperText>{error?.message}</FormHelperText>
                        </Grid>
                      </Grid>
                    </FormControl>
                  )}
                />

                <Controller
                  key="skillIds"
                  name="skillIds"
                  control={control}
                  render={({ field: { name, onBlur, onChange, value } }) => (
                    <FormControl fullWidth>
                      <Grid container item alignItems={'center'} gap={1}>
                        <Grid item xs={3}>
                          <FormLabel>{jobFieldI18n('skillIds')}</FormLabel>
                        </Grid>
                        <Grid item xs overflow={'auto'} whiteSpace={'nowrap'}>
                          <Select
                            id={name}
                            multiple
                            fullWidth
                            displayEmpty
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            renderValue={(selectedSkillIds) =>
                              isEmpty(value) ? (
                                <Typography color="GrayText">{jobFieldI18n('skills.unspecified')}</Typography>
                              ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {map(selectedSkillIds, (selectedSkillId) => {
                                    const selectedSkill = findSkill(selectedSkillId);
                                    return <Chip key={selectedSkill.id} label={selectedSkill.name} />;
                                  })}
                                </Box>
                              )
                            }
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  // 48(ITEM_HEIGHT) * 4.5 + 8(ITEM_PADDING_TOP)
                                  maxHeight: 224,
                                  width: { xs: '100%', sm: 0 },
                                },
                              },
                            }}
                            SelectDisplayProps={{
                              style: {
                                display: 'flex',
                                alignItems: 'center',
                                paddingTop: '4px',
                                paddingBottom: '4px',
                                marginTop: '4px',
                                marginBottom: '4px',
                                minHeight: '48px',
                              },
                            }}
                          >
                            {map(jobCreatingOptionData?.jobCreatingOption?.skills, (skill) => (
                              <MenuItem key={skill.id} value={skill.id} divider>
                                <Checkbox edge="start" checked={includes(value, skill.id)} />
                                <ListItemText
                                  primary={`${skill.name} ( +${jobCreatePageI18n('pricing_per_hour', { price: skill.additionalWagePerHour, joinOutput: true })})`}
                                  primaryTypographyProps={{
                                    variant: 'subtitle2',
                                    fontWeight: 'bold',
                                    sx: { whiteSpace: 'normal' },
                                  }}
                                  secondary={skill.description}
                                  secondaryTypographyProps={{
                                    sx: { whiteSpace: 'normal' },
                                  }}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                        </Grid>
                      </Grid>
                    </FormControl>
                  )}
                />

                <Collapse in={!isEmpty(scheduleDates)} sx={{ width: '100%' }}>
                  <Grid container item alignItems={'center'} gap={1}>
                    <Grid item xs={3}>
                      <FormLabel>{jobFieldI18n('bid_price.rate')} *</FormLabel>
                    </Grid>
                    <Grid item xs overflow={'auto'} whiteSpace={'nowrap'}>
                      {isEmpty(scheduleDates) || isNil(startTime) || isNil(endTime) || isNil(breakDurationMinute) ? (
                        <Stack justifyContent={'center'} height={48}>
                          <FormLabel>{jobFieldI18n('bid_price.detail')}</FormLabel>
                        </Stack>
                      ) : (
                        <RadioButtonGroup
                          color="primary"
                          value={some(bidPrices, ({ isBidPrice }) => isBidPrice)}
                          items={[
                            {
                              value: false,
                              label: (
                                <Stack direction={'row'} alignItems={'center'}>
                                  <Collapse
                                    in={jobPricingLoading || jobPricingNormalLoading}
                                    orientation="horizontal"
                                    unmountOnExit
                                  >
                                    <LoadingSpinner size={16} BoxProps={{ paddingRight: 1 }} />
                                  </Collapse>
                                  {bidPriceRangeString}
                                </Stack>
                              ),
                            },
                            {
                              value: true,
                              label: (
                                <div onClick={() => setShowJobCardsDrawer(true)}>
                                  {jobFieldI18n('bid_price.custom')}
                                </div>
                              ),
                            },
                          ]}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Collapse>

                <Controller
                  key="numberOfPosition"
                  name="numberOfPosition"
                  control={control}
                  render={({
                    field: { value: numberOfPosition, onChange: _onChange, ...field },
                    fieldState: { invalid, error },
                  }) => (
                    <FormControl id={field.name} fullWidth error={invalid}>
                      <Grid container item alignItems={'center'} gap={1}>
                        <Grid item xs={3}>
                          <FormLabel>{jobFieldI18n('number_of_position_period')} *</FormLabel>
                        </Grid>
                        <Grid item xs>
                          <NumberTextField
                            {...field}
                            id={field.name}
                            inputMode="numeric"
                            value={numberOfPosition.toString()}
                            onNumberChange={numberOfPositionHandler}
                            error={invalid}
                            InputProps={{
                              inputProps: { pattern: '[0-9]*', sx: { maxWidth: 64, textAlign: 'center' } },
                              startAdornment: (
                                <InputAdornment position="start">
                                  <IconButton
                                    disabled={(numberOfPosition ?? 0) <= 1}
                                    onClick={() => numberOfPositionHandler((numberOfPosition ?? 0) - 1)}
                                    data-testid="number-of-position-decrement"
                                  >
                                    <Remove />
                                  </IconButton>
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    disabled={
                                      maxNumberOfPosition !== -1 && (numberOfPosition ?? 0) >= maxNumberOfPosition
                                    }
                                    onClick={() => numberOfPositionHandler((numberOfPosition ?? 0) + 1)}
                                    data-testid="number-of-position-increment"
                                  >
                                    <Add />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                      <Grid container item gap={1} justifyContent={'flex-end'}>
                        <Grid item xs={9}>
                          <FormHelperText>{error?.message}</FormHelperText>
                        </Grid>
                      </Grid>
                    </FormControl>
                  )}
                />

                <Controller
                  key="gender"
                  name="gender"
                  control={control}
                  render={({ field: { name, onBlur, onChange, value }, fieldState: { invalid, error } }) => (
                    <FormControl id={name} fullWidth error={invalid}>
                      <Grid container item alignItems={'center'} gap={1}>
                        <Grid item xs={3}>
                          <FormLabel>{jobFieldI18n('gender')} *</FormLabel>
                        </Grid>
                        <Grid item xs overflow={'auto'} whiteSpace={'nowrap'}>
                          <RadioButtonGroup
                            id={name}
                            color="primary"
                            value={value}
                            onChangeValue={onChange}
                            onBlur={onBlur}
                            items={[
                              { label: labelFieldEnum('job.genders', 'male'), value: 'male' },
                              { label: labelFieldEnum('job.genders', 'female'), value: 'female' },
                              { label: labelFieldEnum('job.genders', 'unspecified'), value: 'SET_NULL_ON_SUBMIT' },
                            ]}
                          />
                        </Grid>
                      </Grid>
                      <Grid container item gap={1} justifyContent={'flex-end'}>
                        <Grid item xs={9}>
                          <FormHelperText>{error?.message}</FormHelperText>
                        </Grid>
                      </Grid>
                    </FormControl>
                  )}
                />

                <Controller
                  key="jobType"
                  name="jobType"
                  control={control}
                  render={({ field: { name, onBlur, onChange, value }, fieldState: { invalid, error } }) => (
                    <FormControl id={name} fullWidth error={invalid}>
                      <Grid container item alignItems={'center'} gap={1}>
                        <Grid item xs={3}>
                          <FormLabel>{jobFieldI18n('job_type')} *</FormLabel>
                        </Grid>
                        <Grid item xs overflow={'auto'} whiteSpace={'nowrap'}>
                          <RadioButtonGroup
                            id={name}
                            color="primary"
                            value={value}
                            onChangeValue={onChange}
                            onBlur={onBlur}
                            items={map(jobTypeOptionWithOther, (jobAndDuty) => ({
                              label: labelFieldEnum('job.job_types', jobAndDuty.id),
                              value: jobAndDuty.id,
                            }))}
                          />
                        </Grid>
                      </Grid>
                      <Grid container item gap={1} justifyContent={'flex-end'}>
                        <Grid item xs={9}>
                          <FormHelperText>{error?.message}</FormHelperText>
                        </Grid>
                      </Grid>
                    </FormControl>
                  )}
                />

                <Controller
                  key="needReplacement"
                  name="needReplacement"
                  control={control}
                  render={({ field: { name, value, onChange } }) => (
                    <FormControl id={name} fullWidth>
                      <Grid container item gap={1}>
                        <Grid item xs={12}>
                          <FormLabel>{jobFieldI18n('need_replacement')}</FormLabel>
                        </Grid>
                        <Grid item xs={12} sx={{ margin: -0.5 }}>
                          <RadioGroup
                            row
                            value={value ? 'needed' : 'not_needed'}
                            onChange={(e) => onChange(e.target.value === 'needed')}
                          >
                            <Grid item xs={12} sm={6} padding={0.5}>
                              <RadioCard
                                value="needed"
                                sx={{ height: '100%' }}
                                innerContainerProps={{ height: '100%', alignItems: 'flex-start' }}
                              >
                                <CardContent sx={{ paddingTop: 1.25, paddingLeft: 0 }}>
                                  <Typography variant="h6">{jobCreatePageI18n('need_replacement_needed')}</Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {jobCreatePageI18n('need_replacement_needed_description')}
                                  </Typography>
                                </CardContent>
                              </RadioCard>
                            </Grid>
                            <Grid item xs={12} sm={6} padding={0.5}>
                              <RadioCard
                                value="not_needed"
                                sx={{ height: '100%' }}
                                innerContainerProps={{ height: '100%', alignItems: 'flex-start' }}
                              >
                                <CardContent sx={{ paddingTop: 1.25, paddingLeft: 0 }}>
                                  <Typography variant="h6">
                                    {jobCreatePageI18n('need_replacement_not_needed')}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {jobCreatePageI18n('need_replacement_not_needed_description')}
                                  </Typography>
                                </CardContent>
                              </RadioCard>
                            </Grid>
                          </RadioGroup>
                        </Grid>
                      </Grid>
                    </FormControl>
                  )}
                />

                <Collapse
                  key="duties"
                  in={!isNil(jobType) && jobType !== 'other'}
                  sx={{ width: '100%', marginBottom: -2 }}
                >
                  <Controller
                    key="duties"
                    name="duties"
                    control={control}
                    render={({ field: { name, value: duties }, fieldState: { invalid, error } }) => (
                      <FormControl id={name} fullWidth error={invalid}>
                        <Grid container item gap={1}>
                          <Grid item xs={12}>
                            <FormLabel>{jobFieldI18n('duty')} *</FormLabel>
                          </Grid>
                          <Grid item xs={12}>
                            <FormGroup row>
                              {map(jobDutyOptions?.duties, (duty) => (
                                <Grid key={duty} item xs={12} sm={6}>
                                  <FormControlLabel
                                    label={labelFieldEnum('job.duties', duty)}
                                    value={duty}
                                    control={
                                      <Checkbox
                                        checked={includes(duties, duty)}
                                        onChange={({ target: { checked } }) => jobDutiesHandler(checked, duty)}
                                      />
                                    }
                                    sx={{ paddingLeft: 2 }}
                                  />
                                </Grid>
                              ))}
                            </FormGroup>
                          </Grid>
                          <Grid container item>
                            <FormHelperText>{error?.message}</FormHelperText>
                          </Grid>
                        </Grid>
                      </FormControl>
                    )}
                  />
                </Collapse>

                <Collapse key="remark" in={isNil(jobType) || jobType === 'other'} sx={{ width: '100%', marginTop: -2 }}>
                  <Controller
                    key="remark"
                    name="remark"
                    control={control}
                    render={({ field, fieldState: { invalid, error } }) => (
                      <FormControl id={field.name} fullWidth error={invalid}>
                        <Grid container item gap={1}>
                          <FormLabel>{jobFieldI18n('remark')} *</FormLabel>
                          <TextField
                            id={field.name}
                            type="text"
                            fullWidth
                            multiline
                            minRows={4}
                            maxRows={8}
                            error={invalid}
                            placeholder={join(jobCreatePageI18n('remark_hint'))}
                            helperText={error?.message}
                            {...field}
                          />
                        </Grid>
                      </FormControl>
                    )}
                  />
                </Collapse>

                <Controller
                  key="detail"
                  name="detail"
                  control={control}
                  render={({ field, fieldState: { invalid, error } }) => (
                    <FormControl id={field.name} fullWidth error={invalid}>
                      <Grid container item gap={1}>
                        <FormLabel>{jobFieldI18n('detail')} </FormLabel>
                        <TextField
                          id={field.name}
                          type="text"
                          fullWidth
                          multiline
                          minRows={4}
                          maxRows={8}
                          error={invalid}
                          placeholder={`ตัวอย่าง มีค่ามัดจำผ้ากันเปื้อน 100 บาท `}
                          helperText={error?.message}
                          {...field}
                        />
                      </Grid>
                    </FormControl>
                  )}
                />

                <FormControl fullWidth error={!isNil(uniformErrorMessage)}>
                  <Grid container item>
                    <Stack width="100%" gap={1}>
                      <FormLabel>{jobFieldI18n('uniform')} *</FormLabel>

                      <Collapse in={!isEmpty(jobTypedUniforms)} unmountOnExit>
                        <Controller
                          key="uniformId"
                          name="uniformId"
                          control={control}
                          render={({ field: { value, ...field } }) => (
                            <Select
                              value={value ?? ''}
                              {...field}
                              fullWidth
                              SelectDisplayProps={{
                                style: {
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  paddingTop: '4px',
                                  paddingBottom: '4px',
                                  minHeight: '56px',
                                },
                              }}
                            >
                              {map(jobTypedUniforms, (uniform) => (
                                <MenuItem key={uniform.id} value={uniform.id} divider>
                                  <ListItem disablePadding dense>
                                    <ListItemAvatar>
                                      <Avatar src={uniform.photo}>
                                        <Person />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText primary={uniform.title} />
                                  </ListItem>
                                </MenuItem>
                              ))}

                              <MenuItem key="_OTHER_UNIFORM" value="_OTHER_UNIFORM">
                                <ListItem disablePadding dense>
                                  <ListItemAvatar>
                                    <Avatar>
                                      <Notes />
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText primary={i18n.t('general.custom')} />
                                </ListItem>
                              </MenuItem>
                            </Select>
                          )}
                        />
                      </Collapse>

                      <Collapse in={!isNil(selectedUniform)} unmountOnExit>
                        {!isNil(selectedUniform) && <UniformCard uniform={selectedUniform} />}
                      </Collapse>

                      <Collapse in={uniformId === '_OTHER_UNIFORM'} unmountOnExit>
                        <Controller
                          key="uniform"
                          name="uniform"
                          control={control}
                          render={({ field, fieldState: { invalid } }) => (
                            <TextField
                              id={field.name}
                              type="text"
                              fullWidth
                              multiline
                              minRows={4}
                              maxRows={8}
                              error={invalid}
                              {...field}
                            />
                          )}
                        />
                      </Collapse>
                    </Stack>

                    <FormHelperText>{uniformErrorMessage}</FormHelperText>
                  </Grid>
                </FormControl>

                <Controller
                  key="benefit"
                  name="benefit"
                  control={control}
                  render={({ field, fieldState: { invalid, error } }) => (
                    <FormControl id={field.name} fullWidth error={invalid}>
                      <Grid container item gap={1}>
                        <FormLabel>{jobFieldI18n('benefit')} *</FormLabel>
                        <TextField
                          id={field.name}
                          type="text"
                          fullWidth
                          multiline
                          minRows={4}
                          maxRows={8}
                          error={invalid}
                          placeholder={join(jobCreatePageI18n('benefit_hint'))}
                          helperText={error?.message}
                          {...field}
                        />
                      </Grid>
                    </FormControl>
                  )}
                />

                <Grid container item gap={0.5}>
                  <Typography
                    variant="subtitle1"
                    color="primary"
                    alignItems="center"
                    display="inline-flex"
                    width="100%"
                  >
                    <Star fontSize="small" sx={{ marginRight: 1 }} />
                    {jobCreatePageI18n('create_condition_title_period')}
                  </Typography>

                  <Typography variant="body2" sx={{ opacity: 0.87 }}>
                    {jobCreatePageI18n('create_condition_description_period_1')}
                    <List sx={{ listStyle: 'disc', pl: 4 }}>
                      <ListItem sx={{ display: 'list-item', py: 0 }}>
                        {jobCreatePageI18n('create_condition_description_period_1_1')}
                      </ListItem>
                      <ListItem sx={{ display: 'list-item', py: 0 }}>
                        {jobCreatePageI18n('create_condition_description_period_1_2')}
                      </ListItem>
                      <ListItem sx={{ display: 'list-item', py: 0 }}>
                        {jobCreatePageI18n('create_condition_description_period_1_3')}
                      </ListItem>
                    </List>
                  </Typography>

                  <Typography variant="body2" sx={{ opacity: 0.87 }}>
                    {jobCreatePageI18n('create_condition_description_period_2')}
                    <Typography variant="body2" sx={{ textDecoration: 'underline' }} display={'inline'}>
                      {jobCreatePageI18n('create_condition_description_period_2_underline')}
                    </Typography>
                  </Typography>

                  <Typography variant="body2" sx={{ opacity: 0.87 }}>
                    {jobCreatePageI18n('create_condition_description_period_3')}
                  </Typography>

                  {otHour > 0 ? (
                    <Typography variant="body2" sx={{ opacity: 0.87, mt: 1 }}>
                      {jobCreatePageI18n('create_condition_ot_rate', { ot_hour: otHour })}
                    </Typography>
                  ) : null}

                  <Typography variant="body2" sx={{ opacity: 0.87, mt: 1 }}>
                    {jobCreatePageI18n('create_condition_cancel_period')}
                  </Typography>

                  <Typography variant="body2" sx={{ my: 1, width: '100%' }}>
                    <LinkMui onClick={openDialogHandler} sx={{ '&:hover': { cursor: 'pointer' } }}>
                      {i18n.t('general.see_more_detail')}
                    </LinkMui>
                  </Typography>
                </Grid>

                <Collapse in={!isEmpty(validateErrors)} unmountOnExit sx={{ width: '100%' }}>
                  <Alert severity="error" sx={{ width: '100%' }}>
                    {map(validateErrors, (error, type) => (
                      <Typography key={type}>
                        • {jobFieldI18n(mapValueKeyToI18nField(type))}{' '}
                        {hookFormNestedFieldErrorMessage(error, { joinErrorMessages: false })}
                      </Typography>
                    ))}
                  </Alert>
                </Collapse>

                <Collapse in={!isEmpty(errorMessage)} unmountOnExit sx={{ width: '100%' }}>
                  <Alert severity="error" sx={{ width: '100%' }}>
                    {errorMessage}
                  </Alert>
                </Collapse>
                <Collapse in={hasCreateFailure} unmountOnExit sx={{ width: '100%' }}>
                  <Alert severity="warning" sx={{ width: '100%' }}>
                    {periodJobCreatePageI18n('create_request_failed_message')}
                  </Alert>
                </Collapse>

                <LoadingButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || hasCreateFailure}
                  loading={isSubmitting}
                  data-testid="job-creation-submit-button"
                >
                  {i18n.t('general.done')}
                </LoadingButton>

                <Dialog open={showDialog} onClose={closeDialogHandler} PaperProps={{ sx: { width: '100%' } }}>
                  <DialogTitle flexDirection={'row'} alignItems={'center'} display={'inline-flex'}>
                    <DialogTitle>{jobCreatePageI18n('create_condition_title')}</DialogTitle>
                    <div style={{ flex: 1 }} />
                    <Box>
                      <IconButton onClick={closeDialogHandler}>
                        <Close />
                      </IconButton>
                    </Box>
                  </DialogTitle>
                  <DialogContent>
                    <Grid container gap={2}>
                      <Typography>
                        {jobCreatePageI18n('create_period_condition_detail_1_1')}
                        <Typography sx={{ textDecoration: 'underline' }} display={'inline'}>
                          {jobCreatePageI18n('create_period_condition_detail_1_2_underline')}
                        </Typography>
                        {jobCreatePageI18n('create_period_condition_detail_1_3')}
                      </Typography>
                      <Typography>
                        {jobCreatePageI18n('create_period_condition_detail_2_1')}
                        <Typography sx={{ textDecoration: 'underline' }} display={'inline'}>
                          {jobCreatePageI18n('create_period_condition_detail_2_2_underline')}
                        </Typography>
                        {jobCreatePageI18n('create_period_condition_detail_2_3')}
                      </Typography>
                      <Typography>
                        {jobCreatePageI18n('create_period_condition_detail_3_1')}
                        <Typography sx={{ textDecoration: 'underline' }} display={'inline'}>
                          {jobCreatePageI18n('create_period_condition_detail_3_2_underline')}
                        </Typography>
                        {jobCreatePageI18n('create_period_condition_detail_3_3')}
                      </Typography>
                      <Typography>
                        {jobCreatePageI18n('create_period_condition_detail_4_1')}
                        <Typography sx={{ textDecoration: 'underline' }} display={'inline'}>
                          {jobCreatePageI18n('create_period_condition_detail_4_2_underline')}
                        </Typography>{' '}
                        {jobCreatePageI18n('create_period_condition_detail_4_3')}
                      </Typography>
                      <Grid container item justifyContent={'center'}>
                        <Grid item xs={12} sm={9}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>{jobCreatePageI18n('date_duration')}</TableCell>
                                <TableCell>{jobFieldI18n('cancel_fee')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell>{jobCreatePageI18n('cancel_period_3_days')}</TableCell>
                                <TableCell>
                                  {jobCreatePageI18n('cancel_period_fee_per_day_per_position', {
                                    cancel_fee: get(cancelFees, 'restaurant_cancel_normal'),
                                  })}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>{jobCreatePageI18n('cancel_period_2_days')}</TableCell>
                                <TableCell>
                                  {jobCreatePageI18n('cancel_period_fee_per_day_per_position', {
                                    cancel_fee: get(cancelFees, 'restaurant_cancel_next_day'),
                                  })}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>{jobCreatePageI18n('cancel_period_1_day')}</TableCell>
                                <TableCell>
                                  {jobCreatePageI18n('cancel_period_fee_per_day_per_position', {
                                    cancel_fee: get(cancelFees, 'restaurant_cancel_same_day'),
                                  })}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Grid>
                      </Grid>
                      <Grid container item gap={2} paddingLeft={4}>
                        <Typography>
                          {jobCreatePageI18n('create_period_condition_detail_41_1')}{' '}
                          {jobCreatePageI18n('cancel_period_fee_per_day_per_position', {
                            cancel_fee: get(cancelFees, 'restaurant_cancel_normal'),
                          })}
                        </Typography>
                        <Typography>
                          {jobCreatePageI18n('create_period_condition_detail_42_1')}{' '}
                          {jobCreatePageI18n('cancel_period_fee_per_day_per_position', {
                            cancel_fee: get(cancelFees, 'restaurant_cancel_next_day'),
                          })}
                        </Typography>
                        <Typography>
                          {jobCreatePageI18n('create_period_condition_detail_43_1')}{' '}
                          {jobCreatePageI18n('cancel_period_fee_per_day_per_position', {
                            cancel_fee: get(cancelFees, 'restaurant_cancel_same_day'),
                          })}
                        </Typography>
                      </Grid>
                      <Grid container item paddingLeft={4} paddingTop={4}>
                        <Stack>
                          <Typography fontWeight="bold">{jobCreatePageI18n('create_period_example_title')}</Typography>
                          <Typography>{jobCreatePageI18n('create_period_example_1')}</Typography>
                          <Typography>{jobCreatePageI18n('create_period_example_1_1')}</Typography>
                          <Typography>{jobCreatePageI18n('create_period_example_1_2')}</Typography>
                          <Typography>{jobCreatePageI18n('create_period_example_1_3')}</Typography>
                          <Typography>{jobCreatePageI18n('create_period_example_1_4')}</Typography>
                          <Typography>{jobCreatePageI18n('create_period_example_1_5')}</Typography>
                          <Typography>{jobCreatePageI18n('create_period_example_1_6')}</Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </DialogContent>
                </Dialog>
              </Grid>
            </form>

            <CreditNotEnoughPopup open={isNotEnoughCredit} onClosed={(_) => setIsNotEnoughCredit(false)} />

            <BottomSafeArea />
          </Container>

          <JobCardListDrawer open={showJobCardsDrawer} onClose={() => setShowJobCardsDrawer(false)} />
        </FormProvider>
      </Protector.Allowed>
    </Protector>
  );
};

export default JobCreatePeriodPage;
